import React, { useState } from 'react';
import { SessionSettings } from '../types/index.ts';

interface SessionSettingsModalProps {
  currentSettings?: SessionSettings;
  onClose: () => void;
  onSave: (settings: SessionSettings) => void;
}

const SessionSettingsModal: React.FC<SessionSettingsModalProps> = ({ 
  currentSettings, 
  onClose, 
  onSave 
}) => {
  const [sessionDate, setSessionDate] = useState(currentSettings?.sessionDate || '');
  const [sessionTime, setSessionTime] = useState(currentSettings?.sessionTime || '14:00');
  const [error, setError] = useState('');

  // Get user's timezone info
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  const timezoneDisplay = now.toLocaleDateString('en-US', {
    timeZoneName: 'short',
    timeZone: userTimezone
  }).split(', ')[1]; // Extract just the timezone part

  // Generate default date (first Tuesday of next month)
  const getDefaultDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    
    // Find first Tuesday of the month
    while (nextMonth.getDay() !== 2) { // 2 = Tuesday
      nextMonth.setDate(nextMonth.getDate() + 1);
    }
    
    return nextMonth.toISOString().split('T')[0];
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sessionDate) {
      setError('Session date is required');
      return;
    }

    // Parse the user's local date and time input
    const [year, month, day] = sessionDate.split('-').map(Number);
    const [hours, minutes] = sessionTime.split(':').map(Number);
    
    // Create date in user's local timezone
    const localDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
    const today = new Date();

    if (localDateTime < today) {
      setError('Session date and time must be in the future');
      return;
    }

    // The localDateTime is already in the user's timezone
    // When we call toISOString(), it automatically converts to UTC
    const utcISOString = localDateTime.toISOString();
    const utcDate = utcISOString.split('T')[0]; // YYYY-MM-DD
    const utcTime = utcISOString.split('T')[1].slice(0, 5); // HH:MM

    console.log(`User set: ${sessionDate} ${sessionTime} (local time)`);
    console.log(`Local datetime: ${localDateTime.toString()}`);
    console.log(`UTC datetime: ${utcISOString}`);
    console.log(`Storing as: ${utcDate} ${utcTime} (UTC)`);

    onSave({ 
      sessionDate: utcDate, 
      sessionTime: utcTime 
    });
    onClose();
  };

  const handleUseDefault = () => {
    const defaultDate = getDefaultDate();
    setSessionDate(defaultDate);
    setSessionTime('14:00'); // Set default time to 2pm
    setError('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Set Next Session Date & Time</h3>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
          Your current timezone: {timezoneDisplay} | Current time: {now.toLocaleTimeString()}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Session Date (required):</label>
            <input
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              required
              style={{
                padding: '8px 12px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '150px',
                cursor: 'pointer'
              }}
            />
            <div>
              <button 
                type="button" 
                onClick={handleUseDefault}
                className="default-date-button"
              >
                Use First Tuesday of Next Month
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Session Time (local time - {timezoneDisplay}):</label>
            <input
              type="time"
              value={sessionTime}
              onChange={(e) => setSessionTime(e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '150px',
                cursor: 'pointer'
              }}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="submit">Save Settings</button>
            <button type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionSettingsModal;
